import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, 
  Users, Mail, Phone, MessageSquare, RefreshCw, ArrowRight,
  BarChart3, PieChart, Activity, AlertTriangle, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API}/admin/transaction-dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error('Erreur lors du chargement des statistiques');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${hours.toFixed(1)} h`;
    return `${(hours / 24).toFixed(1)} jours`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        Impossible de charger les statistiques
      </div>
    );
  }

  // Map API response to expected structure
  const summary = {
    total_transactions: data.total_transactions || 0,
    completed: data.funnel?.completed || 0,
    completion_rate: data.rates?.completion || 0,
    in_progress: Math.max(0, (data.total_transactions || 0) - (data.funnel?.completed || 0) - (data.status_breakdown?.expired || 0)),
    expired: data.status_breakdown?.expired || 0,
    expiration_rate: data.rates?.expiration || 0
  };
  
  const status_breakdown = data.status_breakdown || {};
  const confirmation_rates = {
    buyer_confirmation_rate: data.rates?.confirmation || 0,
    seller_confirmation_rate: data.rates?.confirmation || 0,
    both_confirmation_rate: data.rates?.confirmation || 0
  };
  const average_times_hours = {
    creation_to_buyer_confirm: data.avg_processing_hours || 0,
    creation_to_seller_confirm: data.avg_processing_hours || 0,
    total_duration: data.avg_processing_hours || 0
  };
  const conversion_funnel = data.funnel || {};
  const weekly_trend = data.trend_data || [];
  const reminder_effectiveness = data.reminder_effectiveness || {};
  const notification_preferences = {}; // Not available in current API

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de bord des transferts</h2>
          <p className="text-gray-500">Statistiques et analyses en temps réel</p>
        </div>
        <Button onClick={fetchDashboardData} variant="outline" disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-brand-500 to-brand-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-brand-100 text-sm">Total transferts</p>
                <p className="text-3xl font-bold">{summary.total_transactions}</p>
              </div>
              <BarChart3 className="h-10 w-10 text-brand-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Complétées</p>
                <p className="text-3xl font-bold">{summary.completed}</p>
                <p className="text-green-200 text-xs mt-1">{summary.completion_rate}% taux</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">En cours</p>
                <p className="text-3xl font-bold">{summary.in_progress}</p>
              </div>
              <Activity className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Expirées</p>
                <p className="text-3xl font-bold">{summary.expired}</p>
                <p className="text-red-200 text-xs mt-1">{summary.expiration_rate}% taux</p>
              </div>
              <XCircle className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-brand-600" />
              Entonnoir de conversion
            </CardTitle>
            <CardDescription>Progression des transactions à travers les étapes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conversion_funnel.map((step, index) => (
                <div key={step.step} className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{step.step}</span>
                    <span className="text-sm text-gray-500">{step.count} ({step.rate}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2 ${
                        index === 0 ? 'bg-brand-500' :
                        index === conversion_funnel.length - 1 ? 'bg-green-500' :
                        'bg-brand-400'
                      }`}
                      style={{ width: `${Math.max(step.rate, 5)}%` }}
                    >
                      {step.rate > 15 && (
                        <span className="text-white text-xs font-medium">{step.rate}%</span>
                      )}
                    </div>
                  </div>
                  {index < conversion_funnel.length - 1 && (
                    <div className="flex justify-center my-1">
                      <ArrowRight className="h-4 w-4 text-gray-300 rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-brand-600" />
              Taux de confirmation
            </CardTitle>
            <CardDescription>Confirmations par l'acheteur et le vendeur</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Buyer */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-brand-500 rounded-full"></div>
                    <span className="font-medium">Acheteur</span>
                  </div>
                  <span className="text-2xl font-bold text-brand-600">{confirmation_rates.buyer_confirmation_rate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div 
                    className="bg-brand-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${confirmation_rates.buyer_confirmation_rate}%` }}
                  />
                </div>
              </div>

              {/* Seller */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="font-medium">Vendeur</span>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">{confirmation_rates.seller_confirmation_rate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div 
                    className="bg-purple-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${confirmation_rates.seller_confirmation_rate}%` }}
                  />
                </div>
              </div>

              {/* Both */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-medium">Les deux confirmés</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{confirmation_rates.both_confirmation_rate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-full rounded-full transition-all duration-500"
                    style={{ width: `${confirmation_rates.both_confirmation_rate}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Times */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-brand-600" />
              Délais moyens par étape
            </CardTitle>
            <CardDescription>Temps moyen pour chaque étape du processus</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-brand-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                    <span className="text-brand-600 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Création → Confirmation acheteur</p>
                    <p className="text-sm text-gray-500">Délai de confirmation</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-brand-600">
                  {formatHours(average_times_hours.creation_to_buyer_confirm)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Création → Confirmation vendeur</p>
                    <p className="text-sm text-gray-500">Délai de confirmation</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-purple-600">
                  {formatHours(average_times_hours.creation_to_seller_confirm)}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Durée totale moyenne</p>
                    <p className="text-sm text-gray-500">Création → Complétion</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-green-600">
                  {formatHours(average_times_hours.total_duration)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-brand-600" />
              Préférences de communication
            </CardTitle>
            <CardDescription>Comment les utilisateurs préfèrent être contactés</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="font-medium">Email uniquement</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-blue-600">{notification_preferences.email || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">
                    ({summary.total_transactions > 0 ? Math.round((notification_preferences.email || 0) / summary.total_transactions * 100) : 0}%)
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="font-medium">SMS uniquement</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-green-600">{notification_preferences.phone || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">
                    ({summary.total_transactions > 0 ? Math.round((notification_preferences.phone || 0) / summary.total_transactions * 100) : 0}%)
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Zap className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="font-medium">Email + SMS</span>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-purple-600">{notification_preferences.both || 0}</span>
                  <span className="text-gray-500 text-sm ml-1">
                    ({summary.total_transactions > 0 ? Math.round((notification_preferences.both || 0) / summary.total_transactions * 100) : 0}%)
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-brand-600" />
            Tendance hebdomadaire
          </CardTitle>
          <CardDescription>Évolution des transactions sur les 8 dernières semaines</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-600">Semaine</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">Créées</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">Complétées</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">Expirées</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">Taux de complétion</th>
                </tr>
              </thead>
              <tbody>
                {weekly_trend.map((week, index) => (
                  <tr key={week.week} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="py-3 px-2 font-medium">{week.week}</td>
                    <td className="text-center py-3 px-2">{week.created}</td>
                    <td className="text-center py-3 px-2">
                      <span className="text-green-600 font-medium">{week.completed}</span>
                    </td>
                    <td className="text-center py-3 px-2">
                      <span className="text-red-600 font-medium">{week.expired}</span>
                    </td>
                    <td className="text-center py-3 px-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        week.completion_rate >= 70 ? 'bg-green-100 text-green-800' :
                        week.completion_rate >= 40 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {week.completion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Insights */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Expired Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Analyse des expirations
            </CardTitle>
            <CardDescription>Pourquoi les transactions expirent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-gray-700">Acheteur n'a pas confirmé</span>
                <span className="font-bold text-red-600">{expired_analysis.buyer_not_confirmed}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-gray-700">Vendeur n'a pas confirmé</span>
                <span className="font-bold text-red-600">{expired_analysis.seller_not_confirmed}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-100 rounded-lg">
                <span className="text-gray-700">Aucun des deux n'a confirmé</span>
                <span className="font-bold text-red-700">{expired_analysis.neither_confirmed}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reminder Effectiveness */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-600" />
              Efficacité des rappels
            </CardTitle>
            <CardDescription>Impact des rappels automatiques</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <div className="text-5xl font-bold text-amber-600 mb-2">
                {reminder_effectiveness.conversion_rate}%
              </div>
              <p className="text-gray-500 mb-4">Taux de conversion après rappel</p>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{reminder_effectiveness.transactions_with_reminders}</p>
                  <p className="text-sm text-gray-500">Transactions avec rappels</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{reminder_effectiveness.completed_after_reminder}</p>
                  <p className="text-sm text-gray-500">Complétées après rappel</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TransactionDashboard;
